import clsx from 'clsx'
import { computed, defineComponent, ref } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'

import VButton from '@/components/common/button/VButton'
import VTextField from '@/components/common/text-field/VTextField'
import VForm from '@/components/common/VForm'
import VIcon from '@/components/common/VIcon'
import useAuth from '@/composables/auth/useAuth'
import useAxiosResponse from '@/composables/common/useAxiosResponse'
import useLocalization from '@/composables/localization/useLocalization'

export default defineComponent(() => {
  const { localeTexts } = useLocalization()
  const auth = useAuth()
  const route = useRoute()
  const router = useRouter()
  const email = ref('')
  const password = ref('')
  const passwordType = ref<'text' | 'password'>('password')
  const messageError = ref('')
  const hasEmailError = ref(false)
  const hasPasswordError = ref(false)

  const loginMutation = useAxiosResponse(async () => {
    return await auth.login({ email: email.value, password: password.value }, { unauthorizedErrorEnabled: false, requestErrorEnabled: true, requestErrorStatusIgnores: [400, 401] })
  }, {
    onSuccess (data: { access_token: string, refresh_token: string }) {
      auth.setAccessToken(data.access_token)
      auth.setRefreshToken(data.refresh_token)

      if (auth.validateToken()) {
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        if (route.query.redirect) {
          router.replace(route.query.redirect as string)
        } else {
          router.replace('/')
        }
      }
    },
    onError (err) {
      if (err.response && err.response?.status < 500) {
        hasEmailError.value = true
        hasPasswordError.value = true
        messageError.value = 'Incorrect email or password'
      }
    },
    loadingMode: 'normal'
  })

  // #region Getters

  const passwordIconName = computed((): string => {
    return passwordType.value === 'password'
      ? 'material-symbols:visibility-outline'
      : 'material-symbols:visibility-off-outline'
  })

  // #endregion

  // #region Actions

  const handleDisplayPassword = (): void => {
    if (passwordType.value === 'password') {
      passwordType.value = 'text'
      return
    }

    passwordType.value = 'password'
  }

  const handleLogin = async (): Promise<void> => {
    messageError.value = ''
    hasEmailError.value = email.value.trim() === ''
    hasPasswordError.value = password.value.trim() === ''

    if (hasEmailError.value || hasPasswordError.value) {
      messageError.value = 'Require'
      return
    }

    loginMutation.execute()
  }

  // #endregion

  return () => (
    <div class='mx-auto flex w-full pt-30 tablet:w-[370px] tablet:pt-22'>
      <VForm
        class='relative flex w-full flex-col items-center justify-center rounded-lg bg-white p-6 tablet:p-0'
        onSubmit={handleLogin}
      >
        <h1 class='text-heading-4 font-normal text-primary'>{localeTexts.value['signin-heading']}</h1>
        <div class='mt-1 text-body-1 text-neutral'>{localeTexts.value['signin-paragraph']}</div>
        <div class='mt-6 flex w-full flex-col'>
          <VTextField
            t-data='email-text-field'
            type='email'
            label='Email'
            name='email'
            placeholder={localeTexts.value['signin-email-placeholder']}
            variant='floating'
            value={email.value}
            autofocus
            autocomplete='username'
            error={hasEmailError.value}
            required
            onInput={(value) => {
              email.value = value
            }}
          />
        </div>
        <div class='mt-8 flex w-full flex-col'>
          <VTextField
            t-data='password-text-field'
            type={passwordType.value}
            label='Password'
            name='current-password'
            placeholder={localeTexts.value['signin-password-placeholder']}
            autocomplete='current-password'
            variant='floating'
            value={password.value}
            error={hasPasswordError.value}
            required
            onInput={(value) => (password.value = value)}
          >{{
            append () {
              return (
                <button
                  t-data='eye-btn'
                  class='pointer-events-auto'
                  type='button'
                  onClick={handleDisplayPassword}
                >
                  <VIcon
                    class='text-[24px] text-neutral-800'
                    icon={passwordIconName.value}
                  />
                </button>
              )
            }
          }}
          </VTextField>
        </div>
        <div class='mt-5 flex w-full justify-end space-x-2 '>
          <RouterLink
            to='/reset-password'
            class='text-button-3 uppercase text-primary'
          >{localeTexts.value['signin-forgot-btn']}
          </RouterLink>
        </div>
        <div class='mt-4 flex w-full'>
          <VButton
            t-data='log-in-btn'
            class='w-full'
            type='submit'
            color={clsx('btn-primary')}
          >{localeTexts.value['signin-btn']}
          </VButton>
        </div>
        <div class='mt-4 flex w-full justify-center'>
          {messageError.value !== '' &&
            <span
              t-data='login-message-error-text'
              class='text-body-1 text-danger'
            >{messageError.value}
            </span>}
        </div>
        <div class={['flex w-full justify-center', messageError.value !== '' ? 'mt-4' : 'mt-14']}>
          <span class='mr-1 text-body-1'>{localeTexts.value['signin-footer-text']}</span>
          <RouterLink
            to='registration'
            class='text-body-1 text-primary underline'
          >
            {localeTexts.value['signin-register-btn']}
          </RouterLink>
        </div>
      </VForm>
    </div>
  )
}, {
  name: 'LoginView'
})
